import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import PageHeader from '../components/PageHeader';
import { AISidebar, AIRecommendationPanel } from '../components/ai';
import { Info, Users, Heart, Leaf, TrendingUp, AlertCircle } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
// Mock data for segments
const segmentData = [{
  name: 'Studenti',
  value: 145,
  color: '#1a4d2e'
}, {
  name: 'Professionisti',
  value: 98,
  color: '#2d7a4f'
}, {
  name: 'Famiglie',
  value: 67,
  color: '#4aa770'
}, {
  name: 'Senior',
  value: 42,
  color: '#7bc99c'
}];
const segmentTrendData = [{
  month: 'Gen',
  studenti: 120,
  professionisti: 85,
  famiglie: 55,
  senior: 35
}, {
  month: 'Feb',
  studenti: 128,
  professionisti: 90,
  famiglie: 58,
  senior: 38
}, {
  month: 'Mar',
  studenti: 135,
  professionisti: 92,
  famiglie: 62,
  senior: 40
}, {
  month: 'Apr',
  studenti: 145,
  professionisti: 98,
  famiglie: 67,
  senior: 42
}];
const preferencesData = [{
  name: 'Prodotti eco-friendly',
  value: 234
}, {
  name: 'Esperienze sostenibili',
  value: 198
}, {
  name: 'Mobilità verde',
  value: 167
}, {
  name: 'Alimentazione bio',
  value: 145
}, {
  name: 'Energia rinnovabile',
  value: 123
}];
const habitsData = [{
  name: 'Riciclo quotidiano',
  frequency: 'Sempre',
  value: 89
}, {
  name: 'Trasporto pubblico',
  frequency: 'Spesso',
  value: 72
}, {
  name: 'Riduzione plastica',
  frequency: 'Sempre',
  value: 85
}, {
  name: 'Acquisti locali',
  frequency: 'Spesso',
  value: 68
}, {
  name: 'Risparmio energetico',
  frequency: 'Sempre',
  value: 91
}];
const actionsData = [{
  category: 'Mobilità',
  count: 456
}, {
  category: 'Riciclo',
  count: 389
}, {
  category: 'Energia',
  count: 312
}, {
  category: 'Alimentazione',
  count: 278
}, {
  category: 'Acquisti',
  count: 234
}];
const topActionsData = [{
  action: 'Utilizzo bici/monopattino',
  count: 234,
  impact: '+15 kg CO₂ risparmiati'
}, {
  action: 'Riciclo corretto rifiuti',
  count: 198,
  impact: '+12 kg rifiuti recuperati'
}, {
  action: 'Spesa a km zero',
  count: 167,
  impact: '+8 kg CO₂ risparmiati'
}, {
  action: 'Riduzione consumo acqua',
  count: 145,
  impact: '+450 L acqua risparmiati'
}, {
  action: 'Uso energia rinnovabile',
  count: 123,
  impact: '+20 kg CO₂ risparmiati'
}];
// Check if data meets privacy threshold
const PRIVACY_THRESHOLD = 30;
const totalUsers = segmentData.reduce((sum, seg) => sum + seg.value, 0);
const meetsThreshold = totalUsers >= PRIVACY_THRESHOLD;
const Insights = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'audience' | 'preferences' | 'actions'>('audience');
  const [activeAIWidget, setActiveAIWidget] = useState<string | null>(null);
  const PrivacyBanner = () => <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4" data-id="element-1842">
      <div className="flex items-start" data-id="element-1843">
        <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" data-id="element-1844" />
        <div data-id="element-1845">
          <h3 className="text-sm font-medium text-blue-900" data-id="element-1846">Dati Aggregati</h3>
          <p className="mt-1 text-sm text-blue-800" data-id="element-1847">
            Dati aggregati (soglia minima n≥30). Alcuni risultati possono essere
            nascosti per privacy.
          </p>
        </div>
      </div>
    </div>;
  const InsufficientDataCard = () => <div className="rounded-lg border border-gray-200 bg-white p-12" data-id="element-1848">
      <div className="text-center" data-id="element-1849">
        <AlertCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" data-id="element-1850" />
        <h2 className="text-xl font-semibold text-gray-700 mb-2" data-id="element-1851">
          Dati Insufficienti
        </h2>
        <p className="text-gray-500 max-w-md mx-auto" data-id="element-1852">
          Servono almeno 30 utenti nel periodo selezionato.
        </p>
        <p className="text-sm text-gray-400 mt-2" data-id="element-1853">
          Utenti attuali: {totalUsers}
        </p>
      </div>
    </div>;
  // AI Suggestions for each widget - converted to AIRecommendationPanel format
  const getRecommendationsForWidget = (widgetKey: string) => {
    const widgetData: Record<string, any> = {
      segmentDistribution: {
        title: 'Suggerimenti AI: Distribuzione Segmenti',
        recommendations: [{
          id: '1',
          title: 'Crea premi per Studenti',
          type: 'Opportunità',
          diamondRange: 'Segmento 41%',
          reason: 'Segmento dominante. Offri sconti su trasporti pubblici, abbonamenti bike-sharing, o prodotti eco-friendly per università.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }, {
          id: '2',
          title: 'Attiva campagna Senior',
          type: 'Strategia',
          diamondRange: 'Segmento 12%',
          reason: 'Sottorappresentati. Crea premi su risparmio energetico domestico, prodotti per la casa sostenibili, o workshop educativi.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }]
      },
      segmentTrend: {
        title: 'Suggerimenti AI: Trend Segmenti',
        recommendations: [{
          id: '1',
          title: 'Capitalizza crescita Famiglie',
          type: 'Trend',
          diamondRange: '+22% ultimo mese',
          reason: 'Accelerazione forte. Crea bundle famiglia: kit prodotti casa sostenibile, esperienze weekend green, sconti spesa bio.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }, {
          id: '2',
          title: 'Prepara offerte estive Studenti',
          type: 'Previsione',
          diamondRange: 'Picco Maggio-Giugno',
          reason: 'Anticipa premi su mobilità estiva, viaggi sostenibili, eventi outdoor eco-friendly.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }]
      },
      preferences: {
        title: 'Suggerimenti AI: Preferenze',
        recommendations: [{
          id: '1',
          title: 'Espandi catalogo Eco-friendly',
          type: 'Opportunità',
          diamondRange: '234 preferenze',
          reason: 'Domanda altissima. Aggiungi: cosmetici bio, abbigliamento sostenibile, accessori riutilizzabili, prodotti zero-waste.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }, {
          id: '2',
          title: 'Crea esperienze uniche',
          type: 'Strategia',
          diamondRange: '198 preferenze (+18%)',
          reason: 'Offri: workshop eco-design, visite aziende sostenibili, cooking class bio, tour natura guidati.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }]
      },
      habits: {
        title: 'Suggerimenti AI: Abitudini',
        recommendations: [{
          id: '1',
          title: 'Premia abitudini consolidate',
          type: 'Rinforzo',
          diamondRange: '89-91% sempre',
          reason: 'Risparmio energetico e riciclo già forti. Offri: dispositivi smart home, compostiere domestiche, gadget monitoraggio consumi.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }, {
          id: '2',
          title: 'Stimola mobilità sostenibile',
          type: 'Incentivo',
          diamondRange: '72% spesso',
          reason: 'Crea: abbonamenti scontati, bonus bici/monopattini, cashback car-sharing elettrico.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }]
      },
      actionsByCategory: {
        title: 'Suggerimenti AI: Azioni per Categoria',
        recommendations: [{
          id: '1',
          title: 'Potenzia categoria Mobilità',
          type: 'Opportunità',
          diamondRange: '456 azioni (+27%)',
          reason: 'Leader assoluto. Aggiungi sfide: "30 giorni in bici", "Zero auto per una settimana", premi per km sostenibili percorsi.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }, {
          id: '2',
          title: 'Attiva categoria Acquisti',
          type: 'Miglioramento',
          diamondRange: '234 azioni',
          reason: 'Crea: cashback spesa km0, sconti mercati locali, punti extra per prodotti sfusi, partnership negozi bio.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }]
      },
      topActions: {
        title: 'Suggerimenti AI: Top Azioni',
        recommendations: [{
          id: '1',
          title: 'Gamifica top azioni',
          type: 'Strategia',
          diamondRange: '234 completamenti',
          reason: 'Top 3 molto popolari. Crea: badge progressivi, classifiche mensili, premi milestone (10/50/100 azioni), sfide team.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }, {
          id: '2',
          title: 'Diversifica azioni disponibili',
          type: 'Espansione',
          diamondRange: '60% impatto su top 3',
          reason: 'Aggiungi: compostaggio, riparazione oggetti, donazioni, volontariato ambientale.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }]
      }
    };
    return widgetData[widgetKey] || null;
  };
  return <div className="flex h-screen bg-gray-50" data-id="element-1854">
      <div className="w-20 flex-shrink-0" data-id="element-1855">
        <Sidebar data-id="element-1856" />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-1857">
        <Topbar data-id="element-1858" />
        <main className="flex-1 overflow-y-auto p-4 md:p-6" data-id="element-1859">
          <PageHeader title="Insight" subtitle="Preferenze e azioni degli utenti (dati aggregati)." data-id="element-1860" />

          <PrivacyBanner data-id="element-1861" />

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6" data-id="element-1862">
            <nav className="-mb-px flex space-x-8" data-id="element-1863">
              <button onClick={() => setActiveTab('audience')} className={`flex items-center whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'audience' ? 'border-greenback text-greenback' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1864">
                <Users className="h-4 w-4 mr-2" data-id="element-1865" />
                Pubblico
              </button>
              <button onClick={() => setActiveTab('preferences')} className={`flex items-center whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'preferences' ? 'border-greenback text-greenback' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1866">
                <Heart className="h-4 w-4 mr-2" data-id="element-1867" />
                Preferenze e Abitudini
              </button>
              <button onClick={() => setActiveTab('actions')} className={`flex items-center whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'actions' ? 'border-greenback text-greenback' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1868">
                <Leaf className="h-4 w-4 mr-2" data-id="element-1869" />
                Azioni Sostenibili
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          {!meetsThreshold ? <InsufficientDataCard data-id="element-1870" /> : <div className="space-y-6" data-id="element-1871">
              {/* Audience Tab */}
              {activeTab === 'audience' && <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1872">
                    {/* Segment Distribution */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-1873">
                      <div className="flex items-center justify-between mb-4" data-id="element-1874">
                        <h3 className="text-lg font-semibold text-gray-900" data-id="element-1875">
                          Distribuzione Segmenti
                        </h3>
                        <div style={{
                    background: 'linear-gradient(90deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 50%, rgba(116, 10, 214, 1) 100%)',
                    padding: '2px',
                    borderRadius: '100px'
                  }} data-id="element-1876">
                          <button onClick={() => setActiveAIWidget('segmentDistribution')} className="text-black transition-colors flex items-center gap-1.5 bg-white px-3 py-1.5 hover:bg-gray-50" style={{
                      borderRadius: '100px'
                    }} title="AI Insights" data-id="element-1877">
                            <span className="text-sm" data-id="element-1878">Suggerimenti AI</span>
                          </button>
                        </div>
                      </div>
                      <div className="h-80" data-id="element-1879">
                        <ResponsiveContainer width="100%" height="100%" data-id="element-1880">
                          <PieChart data-id="element-1881">
                            <Pie data={segmentData} cx="50%" cy="50%" labelLine={false} label={({
                        name,
                        percent
                      }) => `${name} ${(percent * 100).toFixed(0)}%`} outerRadius={100} fill="#8884d8" dataKey="value" data-id="element-1882">
                              {segmentData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-1883" />)}
                            </Pie>
                            <Tooltip data-id="element-1884" />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="mt-4 grid grid-cols-2 gap-3" data-id="element-1885">
                        {segmentData.map((segment, index) => <div key={index} className="flex items-center" data-id="element-1886">
                            <div className="w-3 h-3 rounded-full mr-2" style={{
                      backgroundColor: segment.color
                    }} data-id="element-1887"></div>
                            <span className="text-sm text-gray-600" data-id="element-1888">
                              {segment.name}: {segment.value}
                            </span>
                          </div>)}
                      </div>
                    </div>

                    {/* Segment Trends */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-1889">
                      <div className="flex items-center justify-between mb-4" data-id="element-1890">
                        <h3 className="text-lg font-semibold text-gray-900" data-id="element-1891">
                          Trend Segmenti nel Tempo
                        </h3>
                        <div style={{
                    background: 'linear-gradient(90deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 50%, rgba(116, 10, 214, 1) 100%)',
                    padding: '2px',
                    borderRadius: '100px'
                  }} data-id="element-1892">
                          <button onClick={() => setActiveAIWidget('segmentTrend')} className="text-black transition-colors flex items-center gap-1.5 bg-white px-3 py-1.5 hover:bg-gray-50" style={{
                      borderRadius: '100px'
                    }} title="AI Insights" data-id="element-1893">
                            <span className="text-sm" data-id="element-1894">Suggerimenti AI</span>
                          </button>
                        </div>
                      </div>
                      <div className="h-80" data-id="element-1895">
                        <ResponsiveContainer width="100%" height="100%" data-id="element-1896">
                          <LineChart data={segmentTrendData} data-id="element-1897">
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-1898" />
                            <XAxis dataKey="month" stroke="#6B7280" fontSize={12} data-id="element-1899" />
                            <YAxis stroke="#6B7280" fontSize={12} data-id="element-1900" />
                            <Tooltip contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px'
                      }} data-id="element-1901" />
                            <Legend data-id="element-1902" />
                            <Line type="monotone" dataKey="studenti" stroke="#1a4d2e" strokeWidth={2} name="Studenti" data-id="element-1903" />
                            <Line type="monotone" dataKey="professionisti" stroke="#2d7a4f" strokeWidth={2} name="Professionisti" data-id="element-1904" />
                            <Line type="monotone" dataKey="famiglie" stroke="#4aa770" strokeWidth={2} name="Famiglie" data-id="element-1905" />
                            <Line type="monotone" dataKey="senior" stroke="#7bc99c" strokeWidth={2} name="Senior" data-id="element-1906" />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>

                  {/* Summary Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1907">
                    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-1908">
                      <p className="text-sm text-gray-500" data-id="element-1909">Utenti Totali</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1910">
                        {totalUsers}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-1911">
                      <p className="text-sm text-gray-500" data-id="element-1912">
                        Segmento Principale
                      </p>
                      <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1913">
                        Studenti
                      </p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-1914">
                      <p className="text-sm text-gray-500" data-id="element-1915">Crescita Mensile</p>
                      <p className="text-2xl font-bold text-green-600 mt-1" data-id="element-1916">
                        +8.5%
                      </p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-1917">
                      <p className="text-sm text-gray-500" data-id="element-1918">Tasso Attività</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1919">
                        73%
                      </p>
                    </div>
                  </div>
                </>}

              {/* Preferences Tab */}
              {activeTab === 'preferences' && <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1920">
                    {/* Main Preferences */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-1921">
                      <div className="flex items-center justify-between mb-4" data-id="element-1922">
                        <h3 className="text-lg font-semibold text-gray-900" data-id="element-1923">
                          Preferenze Principali
                        </h3>
                        <div style={{
                    background: 'linear-gradient(90deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 50%, rgba(116, 10, 214, 1) 100%)',
                    padding: '2px',
                    borderRadius: '100px'
                  }} data-id="element-1924">
                          <button onClick={() => setActiveAIWidget('preferences')} className="text-black transition-colors flex items-center gap-1.5 bg-white px-3 py-1.5 hover:bg-gray-50" style={{
                      borderRadius: '100px'
                    }} title="AI Insights" data-id="element-1925">
                            <span className="text-sm" data-id="element-1926">Suggerimenti AI</span>
                          </button>
                        </div>
                      </div>
                      <div className="h-80" data-id="element-1927">
                        <ResponsiveContainer width="100%" height="100%" data-id="element-1928">
                          <BarChart data={preferencesData} layout="vertical" data-id="element-1929">
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-1930" />
                            <XAxis type="number" stroke="#6B7280" fontSize={12} data-id="element-1931" />
                            <YAxis type="category" dataKey="name" stroke="#6B7280" fontSize={12} width={150} data-id="element-1932" />
                            <Tooltip contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px'
                      }} data-id="element-1933" />
                            <Bar dataKey="value" fill="#1a4d2e" radius={[0, 4, 4, 0]} data-id="element-1934" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Habits */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-1935">
                      <div className="flex items-center justify-between mb-4" data-id="element-1936">
                        <h3 className="text-lg font-semibold text-gray-900" data-id="element-1937">
                          Abitudini Dichiarate
                        </h3>
                        <div style={{
                    background: 'linear-gradient(90deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 50%, rgba(116, 10, 214, 1) 100%)',
                    padding: '2px',
                    borderRadius: '100px'
                  }} data-id="element-1938">
                          <button onClick={() => setActiveAIWidget('habits')} className="text-black transition-colors flex items-center gap-1.5 bg-white px-3 py-1.5 hover:bg-gray-50" style={{
                      borderRadius: '100px'
                    }} title="AI Insights" data-id="element-1939">
                            <span className="text-sm" data-id="element-1940">Suggerimenti AI</span>
                          </button>
                        </div>
                      </div>
                      <div className="space-y-4" data-id="element-1941">
                        {habitsData.map((habit, index) => <div key={index} data-id="element-1942">
                            <div className="flex justify-between items-center mb-2" data-id="element-1943">
                              <span className="text-sm font-medium text-gray-900" data-id="element-1944">
                                {habit.name}
                              </span>
                              <div className="flex items-center" data-id="element-1945">
                                <span className="text-xs text-gray-500 mr-2" data-id="element-1946">
                                  {habit.frequency}
                                </span>
                                <span className="text-sm font-semibold text-greenback" data-id="element-1947">
                                  {habit.value}%
                                </span>
                              </div>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1948">
                              <div className="bg-greenback h-2 rounded-full" style={{
                        width: `${habit.value}%`
                      }} data-id="element-1949"></div>
                            </div>
                          </div>)}
                      </div>
                      <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-1950">
                        <p className="text-xs text-gray-500" data-id="element-1951">
                          Scala frequenza: Sempre (80-100%) • Spesso (60-79%) •
                          A volte (40-59%) • Raramente (&lt;40%)
                        </p>
                      </div>
                    </div>
                  </div>
                </>}

              {/* Actions Tab */}
              {activeTab === 'actions' && <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1952">
                    {/* Actions by Category */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-1953">
                      <div className="flex items-center justify-between mb-4" data-id="element-1954">
                        <h3 className="text-lg font-semibold text-gray-900" data-id="element-1955">
                          Azioni Completate per Categoria
                        </h3>
                        <div style={{
                    background: 'linear-gradient(90deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 50%, rgba(116, 10, 214, 1) 100%)',
                    padding: '2px',
                    borderRadius: '100px'
                  }} data-id="element-1956">
                          <button onClick={() => setActiveAIWidget('actionsByCategory')} className="text-black transition-colors flex items-center gap-1.5 bg-white px-3 py-1.5 hover:bg-gray-50" style={{
                      borderRadius: '100px'
                    }} title="AI Insights" data-id="element-1957">
                            <span className="text-sm" data-id="element-1958">Suggerimenti AI</span>
                          </button>
                        </div>
                      </div>
                      <div className="h-80" data-id="element-1959">
                        <ResponsiveContainer width="100%" height="100%" data-id="element-1960">
                          <BarChart data={actionsData} data-id="element-1961">
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-1962" />
                            <XAxis dataKey="category" stroke="#6B7280" fontSize={12} data-id="element-1963" />
                            <YAxis stroke="#6B7280" fontSize={12} data-id="element-1964" />
                            <Tooltip contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px'
                      }} data-id="element-1965" />
                            <Bar dataKey="count" fill="#1a4d2e" radius={[4, 4, 0, 0]} data-id="element-1966" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Top Actions */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-1967">
                      <div className="flex items-center justify-between mb-4" data-id="element-1968">
                        <h3 className="text-lg font-semibold text-gray-900" data-id="element-1969">
                          Top Azioni del Periodo
                        </h3>
                        <div style={{
                    background: 'linear-gradient(90deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 50%, rgba(116, 10, 214, 1) 100%)',
                    padding: '2px',
                    borderRadius: '100px'
                  }} data-id="element-1970">
                          <button onClick={() => setActiveAIWidget('topActions')} className="text-black transition-colors flex items-center gap-1.5 bg-white px-3 py-1.5 hover:bg-gray-50" style={{
                      borderRadius: '100px'
                    }} title="AI Insights" data-id="element-1971">
                            <span className="text-sm" data-id="element-1972">Suggerimenti AI</span>
                          </button>
                        </div>
                      </div>
                      <div className="space-y-3" data-id="element-1973">
                        {topActionsData.map((action, index) => <div key={index} className="flex items-start p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1974">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-greenback text-white flex items-center justify-center font-semibold text-sm mr-3" data-id="element-1975">
                              {index + 1}
                            </div>
                            <div className="flex-1" data-id="element-1976">
                              <div className="flex items-center justify-between" data-id="element-1977">
                                <p className="font-medium text-gray-900 text-sm" data-id="element-1978">
                                  {action.action}
                                </p>
                                <span className="text-sm font-semibold text-greenback" data-id="element-1979">
                                  {action.count}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1 flex items-center" data-id="element-1980">
                                <TrendingUp className="h-3 w-3 mr-1" data-id="element-1981" />
                                {action.impact}
                              </p>
                            </div>
                          </div>)}
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1982">
                        <div className="flex items-center justify-between text-sm" data-id="element-1983">
                          <span className="text-gray-500" data-id="element-1984">Totale azioni:</span>
                          <span className="font-semibold text-gray-900" data-id="element-1985">
                            1,669
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Impact Summary */}
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200 p-6" data-id="element-1986">
                    <div className="flex items-start" data-id="element-1987">
                      <Leaf className="h-6 w-6 text-green-600 mt-1 mr-3 flex-shrink-0" data-id="element-1988" />
                      <div data-id="element-1989">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1990">
                          Impatto Aggregato del Periodo
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4" data-id="element-1991">
                          <div data-id="element-1992">
                            <p className="text-sm text-gray-600" data-id="element-1993">
                              CO₂ Risparmiata
                            </p>
                            <p className="text-2xl font-bold text-green-600" data-id="element-1994">
                              +68 kg
                            </p>
                          </div>
                          <div data-id="element-1995">
                            <p className="text-sm text-gray-600" data-id="element-1996">
                              Rifiuti Recuperati
                            </p>
                            <p className="text-2xl font-bold text-green-600" data-id="element-1997">
                              +45 kg
                            </p>
                          </div>
                          <div data-id="element-1998">
                            <p className="text-sm text-gray-600" data-id="element-1999">
                              Acqua Risparmiata
                            </p>
                            <p className="text-2xl font-bold text-green-600" data-id="element-2000">
                              +1,200 L
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>}
            </div>}
        </main>
      </div>

      {/* AI Sidebar - Using consistent AIRecommendationPanel */}
      {meetsThreshold && activeAIWidget && <AISidebar isOpen={!!activeAIWidget} onClose={() => setActiveAIWidget(null)} data-id="element-2001">
          {(() => {
        const widgetData = getRecommendationsForWidget(activeAIWidget);
        return widgetData ? <AIRecommendationPanel title={widgetData.title} recommendations={widgetData.recommendations} lastUpdated="Ultimi 30 giorni" data-id="element-2002" /> : null;
      })()}
        </AISidebar>}
    </div>;
};
export default Insights;